<script setup>
import { computed, ref, onMounted, onUnmounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { priorityMeta, priorityRank } from '../constants/priorityColors';
import { useTaskWeekDrag } from '../composables/useTaskWeekDrag';
import { computeOverlapLayout } from 'dashboard/modules/agenda/composables/useOverlapLayout';

const props = defineProps({
  filters: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['taskOpen']);

const store = useStore();
const { t } = useI18n();

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const TASK_BLOCK_MINUTES = 30;

const currentDate = ref(new Date());
const scrollRef = ref(null);
const columnRefs = ref([]);
const allDayRefs = ref([]);

function dateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function dueDayKey(epochSeconds) {
  const d = new Date(epochSeconds * 1000);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function hasTime(task) {
  const d = new Date(task.due_date * 1000);
  return d.getHours() !== 0 || d.getMinutes() !== 0;
}

function timeLabel(task) {
  const d = new Date(task.due_date * 1000);
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

function minutesSinceMidnight(epochSeconds) {
  const d = new Date(epochSeconds * 1000);
  return d.getHours() * 60 + d.getMinutes();
}

const visibleDays = computed(() => {
  const days = [];
  const start = new Date(currentDate.value);
  const dayOfWeek = start.getDay();
  const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  start.setDate(start.getDate() + diff);
  for (let i = 0; i < 7; i += 1) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
});

watch(visibleDays, () => {
  columnRefs.value = [];
  allDayRefs.value = [];
});

const rangeLabel = computed(() => {
  const days = visibleDays.value;
  const from = days[0];
  const to = days[6];
  const fmt = d =>
    d.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' });
  return `${fmt(from)} – ${fmt(to)}`;
});

function goToPreviousWeek() {
  const d = new Date(currentDate.value);
  d.setDate(d.getDate() - 7);
  currentDate.value = d;
}

function goToNextWeek() {
  const d = new Date(currentDate.value);
  d.setDate(d.getDate() + 7);
  currentDate.value = d;
}

function loadWeekTasks() {
  const days = visibleDays.value;
  const pad = n => String(n).padStart(2, '0');
  const fmt = d =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  store.dispatch('tasks/fetchTasks', {
    ...props.filters,
    due_date_from: `${fmt(days[0])}T00:00:00`,
    due_date_to: `${fmt(days[6])}T23:59:59`,
  });
}

onMounted(loadWeekTasks);
watch(currentDate, loadWeekTasks);
watch(() => props.filters, loadWeekTasks, { deep: true });

const allTasks = computed(() => store.getters['tasks/allTasks'] ?? []);

function tasksForDay(day) {
  const key = dateKey(day);
  return allTasks.value.filter(
    task => task.due_date && dueDayKey(task.due_date) === key
  );
}

function timedTasksForDay(day) {
  return tasksForDay(day).filter(hasTime);
}

function allDayTasksForDay(day) {
  return tasksForDay(day)
    .filter(task => !hasTime(task))
    .sort((a, b) => priorityRank(a.priority) - priorityRank(b.priority));
}

function taskColor(task) {
  return priorityMeta(task.priority);
}

function formatHour(hour) {
  return `${String(hour).padStart(2, '0')}:00`;
}

const todayKey = computed(() => dateKey(new Date()));

function nowMinutes() {
  const now = new Date();
  return now.getHours() * 60 + now.getMinutes();
}

const currentMinutes = ref(nowMinutes());
let nowTimer = null;

onMounted(() => {
  nowTimer = setInterval(() => {
    currentMinutes.value = nowMinutes();
  }, 60000);
  if (scrollRef.value) {
    scrollRef.value.scrollTop = Math.max(0, currentMinutes.value - 60);
  }
});

onUnmounted(() => clearInterval(nowTimer));

const { onTaskMousedown, dragState } = useTaskWeekDrag({
  visibleDays,
  columnRefs,
  allDayRefs,
  priorityBarStyle: task => {
    const meta = taskColor(task);
    return { className: meta.bar ?? '', style: meta.barStyle ?? {} };
  },
});

function toOverlapRange(task) {
  const start = new Date(task.due_date * 1000);
  const end = new Date(start.getTime() + TASK_BLOCK_MINUTES * 60000);
  return { ...task, start_at: start.toISOString(), end_at: end.toISOString() };
}

const overlapLayoutByDay = computed(() => {
  const map = new Map();
  visibleDays.value.forEach(day => {
    const ranged = timedTasksForDay(day).map(toOverlapRange);
    map.set(dateKey(day), computeOverlapLayout(ranged));
  });
  return map;
});

function taskLayout(task, day) {
  const top = minutesSinceMidnight(task.due_date);
  const overlap = overlapLayoutByDay.value.get(dateKey(day))?.get(task.id);
  return {
    top,
    height: TASK_BLOCK_MINUTES,
    left: overlap?.left ?? 0,
    width: overlap?.width ?? 100,
    zIndex: overlap?.zIndex ?? 10,
  };
}
</script>

<template>
  <div class="flex flex-col flex-1 overflow-hidden">
    <!-- Navegação de semana -->
    <div
      class="flex items-center justify-center gap-4 py-2 border-b border-n-weak flex-shrink-0"
    >
      <button
        class="i-lucide-chevron-left size-4 text-n-slate-11 hover:text-n-slate-12"
        @click="goToPreviousWeek"
      />
      <span class="text-sm font-medium text-n-slate-12 min-w-40 text-center">
        {{ rangeLabel }}
      </span>
      <button
        class="i-lucide-chevron-right size-4 text-n-slate-11 hover:text-n-slate-12"
        @click="goToNextWeek"
      />
    </div>

    <!-- Cabeçalho fixo dos dias -->
    <div class="flex flex-shrink-0 border-b border-n-weak bg-n-background">
      <div class="w-14 flex-shrink-0 border-r border-n-weak" />
      <div class="flex flex-1">
        <div
          v-for="day in visibleDays"
          :key="dateKey(day)"
          class="flex-1 min-w-[100px] border-r border-n-weak last:border-r-0 h-10 flex flex-col items-center justify-center"
        >
          <span class="text-xs text-n-slate-10 uppercase">
            {{ day.toLocaleDateString('pt-BR', { weekday: 'short' }) }}
          </span>
          <span
            class="text-sm font-medium leading-tight"
            :class="
              dateKey(day) === todayKey ? 'text-n-brand' : 'text-n-slate-12'
            "
          >
            {{ day.getDate() }}
          </span>
        </div>
      </div>
    </div>

    <!-- Faixa "sem horário" -->
    <div class="flex flex-shrink-0 border-b border-n-weak">
      <div
        class="w-14 flex-shrink-0 border-r border-n-weak flex items-center justify-end pr-2 py-1"
      >
        <span class="text-[10px] text-n-slate-10">{{
          t('TASKS.CALENDAR_VIEW.ALL_DAY')
        }}</span>
      </div>
      <div class="flex flex-1">
        <div
          v-for="(day, dayIndex) in visibleDays"
          :key="dateKey(day)"
          :ref="
            el => {
              if (el) allDayRefs[dayIndex] = el;
            }
          "
          class="flex-1 min-w-[100px] border-r border-n-weak last:border-r-0 p-1 flex flex-col gap-0.5 min-h-[32px]"
          :class="
            dragState.targetDayKey === dateKey(day) && dragState.targetIsAllDay
              ? 'bg-n-alpha-2'
              : ''
          "
        >
          <div
            v-for="task in allDayTasksForDay(day)"
            :key="task.id"
            class="truncate rounded px-1 text-xs cursor-pointer text-white"
            :class="[
              taskColor(task).bar,
              task.status === 'completed' ? 'opacity-40 line-through' : '',
              dragState.draggingId === task.id ? '!opacity-30' : '',
            ]"
            :style="taskColor(task).barStyle"
            @click.stop="emit('taskOpen', task.id)"
            @mousedown="onTaskMousedown($event, task, $event.currentTarget)"
          >
            {{ task.title }}
          </div>
        </div>
      </div>
    </div>

    <!-- Corpo scrollável -->
    <div ref="scrollRef" class="flex flex-1 overflow-auto">
      <!-- Coluna de horários -->
      <div class="w-14 flex-shrink-0 border-r border-n-weak">
        <div class="relative">
          <div
            v-for="hour in HOURS"
            :key="hour"
            class="h-[60px] border-b border-n-weak flex items-start justify-end pr-2 pt-1"
          >
            <span class="text-xs text-n-slate-10">{{ formatHour(hour) }}</span>
          </div>
        </div>
      </div>

      <!-- Colunas dos dias -->
      <div class="flex flex-1">
        <div
          v-for="(day, dayIndex) in visibleDays"
          :key="dateKey(day)"
          :ref="
            el => {
              if (el) columnRefs[dayIndex] = el;
            }
          "
          class="flex-1 min-w-[100px] border-r border-n-weak last:border-r-0"
        >
          <div class="relative">
            <div
              v-for="hour in HOURS"
              :key="hour"
              class="h-[60px] border-b border-n-weak"
              :class="
                dragState.targetDayKey === dateKey(day) &&
                !dragState.targetIsAllDay
                  ? 'bg-n-alpha-1'
                  : ''
              "
            />

            <!-- Linha "agora" -->
            <div
              v-if="dateKey(day) === todayKey"
              class="absolute left-0 right-0 z-30 pointer-events-none"
              :style="{ top: `${currentMinutes}px` }"
            >
              <div class="flex items-center">
                <div
                  class="size-2 rounded-full bg-n-ruby-9 -ml-1 flex-shrink-0"
                />
                <div class="flex-1 h-px bg-n-ruby-9" />
              </div>
            </div>

            <!-- Tarefas com horário -->
            <div
              v-for="task in timedTasksForDay(day)"
              :key="task.id"
              class="absolute rounded px-1 cursor-grab text-xs overflow-hidden text-white flex items-center gap-1"
              :class="[
                taskColor(task).bar,
                task.status === 'completed' ? 'opacity-40 line-through' : '',
                dragState.draggingId === task.id ? '!opacity-30' : '',
              ]"
              :style="{
                top: `${taskLayout(task, day).top}px`,
                height: `${taskLayout(task, day).height}px`,
                left: `${taskLayout(task, day).left}%`,
                width: `calc(${taskLayout(task, day).width}% - 2px)`,
                zIndex: taskLayout(task, day).zIndex,
                ...taskColor(task).barStyle,
              }"
              @mousedown.stop="
                onTaskMousedown($event, task, $event.currentTarget)
              "
              @click.stop="
                dragState.draggingId === null && emit('taskOpen', task.id)
              "
            >
              <span class="opacity-90 flex-shrink-0">{{
                timeLabel(task)
              }}</span>
              <span class="truncate">{{ task.title }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
